import { useState } from "react";
import { UserCircle, Edit, Scale, Building2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import type { UserWithProfile } from "@/types/user-profile";
import { DEFAULT_EVALUATION_WEIGHT, useSaveEvaluationWeight } from "@/hooks/use-save-evaluation-weight";
import { ResetPasswordModal } from "./ResetPasswordModal";

interface UserProfileCardProps {
  user: UserWithProfile;
  onChangeProfile: (user: UserWithProfile) => void;
}

export function UserProfileCard({ user, onChangeProfile }: UserProfileCardProps) {
  const { save, isSaving: isSavingWeight } = useSaveEvaluationWeight(user);
  const [weight, setWeight] = useState<number>(user.evaluation_weight ?? DEFAULT_EVALUATION_WEIGHT);
  const hasWeightChanged = weight !== (user.evaluation_weight ?? DEFAULT_EVALUATION_WEIGHT);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  const handleSaveWeight = () => save(weight);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <UserCircle className="h-8 w-8 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg font-bold">
              {user.full_name || user.username || "Sem nome"}
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 text-sm">
          {user.email && (
            <div>
              <span className="font-medium">Email:</span> {user.email}
            </div>
          )}
          <div>
            <span className="font-medium">Perfil:</span>{" "}
            {user.access_profile_name ? (
              <Badge variant="default">{user.access_profile_name}</Badge>
            ) : (
              <Badge variant="secondary">Sem perfil</Badge>
            )}
          </div>
          {user.agencia_nome && (
            <div className="flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">Agência:</span>{" "}
              <Badge variant="outline">{user.agencia_nome}</Badge>
            </div>
          )}
          {user.access_profile_screens && (
            <div>
              <span className="font-medium">Permissões:</span>{" "}
              {user.access_profile_screens.length} telas
            </div>
          )}
        </div>

        {/* Peso de Avaliação */}
        <div className="pt-2 border-t space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1 text-sm font-medium">
              <Scale className="h-4 w-4" />
              Peso de Avaliação
            </Label>
            <Badge variant="outline" className="font-mono">
              {weight}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Slider
              min={1}
              max={10}
              step={1}
              value={[weight]}
              onValueChange={(vals) => setWeight(vals[0])}
              className="flex-1"
            />
            {hasWeightChanged && (
              <Button
                size="sm"
                variant="default"
                onClick={handleSaveWeight}
                disabled={isSavingWeight}
              >
                {isSavingWeight ? "..." : "Salvar"}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Peso usado para cálculo de médias ponderadas nas avaliações.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 min-w-fit"
            onClick={() => setIsResetPasswordOpen(true)}
          >
            <KeyRound className="h-4 w-4 mr-2" />
            Redefinir senha
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 min-w-fit"
            onClick={() => onChangeProfile(user)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Alterar Perfil
          </Button>
        </div>
      </CardContent>
      <ResetPasswordModal
        user={user}
        open={isResetPasswordOpen}
        onOpenChange={setIsResetPasswordOpen}
      />
    </Card>
  );
}
